import { useEffect, useCallback } from 'react';
import { useRecoilState } from 'recoil';
import { dateRangeState, dateValueState } from '../state/statistics.state';

interface UseOptimalDateRangeProps {
  lastProcessedTimestamp?: string | null;
  hasAdjustedDateRange: boolean;
  setHasAdjustedDateRange: (value: boolean) => void;
}

export const useOptimalDateRange = ({
  lastProcessedTimestamp,
  hasAdjustedDateRange,
  setHasAdjustedDateRange,
}: UseOptimalDateRangeProps) => {
  const [, setDateRange] = useRecoilState(dateRangeState);
  const [, setDateValue] = useRecoilState(dateValueState);

  const getLastDataReceivedDate = useCallback((): Date | null => {
    if (!lastProcessedTimestamp) return null;
    const date = new Date(lastProcessedTimestamp);
    return isNaN(date.getTime()) ? null : date;
  }, [lastProcessedTimestamp]);

  const calculateOptimalDateRange = useCallback((lastDataDate: Date): { startDate: Date; endDate: Date } => {
    const thirtyDaysFromLastData = new Date(lastDataDate);
    thirtyDaysFromLastData.setDate(thirtyDaysFromLastData.getDate() - 30);
    return {
      startDate: thirtyDaysFromLastData,
      endDate: lastDataDate,
    };
  }, []);

  useEffect(() => {
    if (hasAdjustedDateRange) return;

    const lastDataDate = getLastDataReceivedDate();
    if (lastDataDate) {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      if (lastDataDate <= thirtyDaysAgo) {
        const optimalRange = calculateOptimalDateRange(lastDataDate);
        setDateRange(optimalRange);
        setDateValue(3);
        setHasAdjustedDateRange(true);
      }
    }
  }, [
    lastProcessedTimestamp,
    setDateRange,
    setDateValue,
    hasAdjustedDateRange,
    getLastDataReceivedDate,
    calculateOptimalDateRange,
    setHasAdjustedDateRange,
  ]);

  return {
    getLastDataReceivedDate,
    calculateOptimalDateRange,
  };
};
